export type AuthMethod = 'google' | 'microsoft' | 'otp' | 'scb';

export interface AuthMethodsResponse {
   methods: AuthMethod[];
}

/**
 * Validates if a string is a valid email address
 */
export const isValidEmail = (email: string): boolean => {
   const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
   return emailRegex.test(email);
};

/**
 * Number of digits of the OTP code sent by email. Single source of truth for
 * input length, validation and user-facing copy.
 */
export const OTP_CODE_LENGTH = 6;

/**
 * Normalizes an OTP code as the user types or pastes it: digits only, capped at
 * OTP_CODE_LENGTH. Codes copied from the email often carry spaces, newlines or
 * surrounding text, so the field is sanitized instead of just validated.
 *
 * @param value - The raw input value
 * @returns The value stripped down to at most OTP_CODE_LENGTH digits
 */
export const sanitizeOtpCode = (value: string): string => value.replaceAll(/\D/g, '').slice(0, OTP_CODE_LENGTH);

/**
 * Validates an OTP code: exactly OTP_CODE_LENGTH digits.
 *
 * @param code - The code to validate
 * @returns True when the code can be submitted
 */
export const isValidOtpCode = (code: string): boolean => new RegExp(`^\\d{${OTP_CODE_LENGTH}}$`).test(code);

/**
 * Determines which authentication methods are available for a given email domain.
 *
 * @param email - The user's email address
 * @returns An object containing the list of available authentication methods
 */
export const getAvailableAuthMethods = (email: string): AuthMethodsResponse => {
   const domain = email.toLowerCase().split('@')[1];

   if (!domain) {
      return { methods: ['google', 'microsoft', 'otp'] };
   }

   // SC domain gets SCB and OTP
   if (domain === 'sc.com' || domain === 'scbdev.com') {
      return { methods: ['scb', 'otp'] };
   }

   // Metrika domain gets only Google and OTP
   if (domain === 'metrika.co') {
      return { methods: ['google', 'otp'] };
   }

   // All other domains get all three options
   return { methods: ['google', 'microsoft', 'otp'] };
};
